import { useCallback, useContext, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { isFunction, isUndefined } from 'lodash'
import { notification } from 'antd'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons'

import DragTable from '../../../../Components/antd/Tables/DragTable/DragTable'
import { ColorsPalette } from '../../../../config/colors'
import { areQuotaEquivalent } from '../../../../utils/entities/quota'
import { QuotaFormModalContext, Types } from '../../../../Providers/Forms/QuotaForm/QuotaFormModalProvider'
import { firstOrDefault, isNonEmptyArray, isObject, isOptimistic, isPositive } from '../../../../utils'
import { getTimezoneLessMoment } from '../../../../utils/momentjs'
import useQuota from '../../../../utils/api/hooks/useQuota'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import { colorShade, getLuminanceFromColorCode } from '../../../../utils/colors'
import SectionTooltip from './SectionTooltip'
import SchoolQuotasDrawer from './SchoolQuotasDrawer'
import ClickableIcon from '../../../../Components/antd/Buttons/ClickableIcon'
import SchoolQuotaDisplayer from './SchoolQuotaDisplayer'
import { getDivStyle, getKeyConfig, getLaneConfig, getLaneKey, getQuotasMatrix, getQuotasPeriods, getSeed, INCLUSION_LINE_KEY, LaneTypes, quotaEvent, sortLanes } from '../../../../Components/shared/Quotas/utils'
import { QuotaFormModes } from '../../../../Providers/Forms/QuotaForm/QuotaForm'
import { onError } from '../../../../utils/apiHelper'
import { Actions, dataUpsert, deleteData, Events, newSelection } from '../../../../utils/events'
import PopoverIconButton from '../../../../Components/antd/Buttons/PopoverIconButton'
import HighlightedColumnsProvider from '../../../../Providers/Quotas/HighlightedColumnsProvider'
import { Axes } from '../../../../Components/antd/Tables/DragTable/ResizingDiv'
import LOCAL_STORAGE_KEYS from '../../../../utils/localStorage'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { includeSection } from '../../../../utils/entities/sectionGroup'

const DEFAULT_COLOR = {
  border: ColorsPalette.GREY,
  background: colorShade(ColorsPalette.GREEN, 100),
  text: getLuminanceFromColorCode(ColorsPalette.GREEN)
}

const SchoolQuotasTable = ({
  holidayPeriods, isYearly, period, quotasMap, sector, school, selectedQuotas, studentPathways, sectionGroups, tableConfig,
  onChange, onSelect
}) => {
  const { setFormHandler } = useContext(QuotaFormModalContext)
  const { coloredSchools } = useContext(InstitutionContext)
  const t = useSelector(selectTranslate)

  const [nullQuotasKey, setNullQuotasKey] = useState(null)

  const quotaHandler = useQuota({ callback: onChange })

  const periods = useMemo(() => getQuotasPeriods(period, t), [t, period])

  const colorsMap = useMemo(() => coloredSchools.reduce((acc, school) => {
    acc[school.id] = school.color ?? DEFAULT_COLOR

    return acc
  }, {}), [coloredSchools])

  const lines = useMemo(() => {
    const { SECTION, GROUP } = LaneTypes

    const nullAcc = quotasMap.quotasNull.reduce((acc, quota) => {
      const { type, id } = getLaneConfig(quota)
      const key = getLaneKey(type, id)

      if (!acc[key]) {
        acc[key] = true
      }

      return acc
    }, {})

    const linesConfig = quotasMap.places.reduce((acc, quota) => {
      const { type, id, name } = getLaneConfig(quota)
      const key = id === INCLUSION_LINE_KEY ? id : getLaneKey(type, id)

      if (!acc[key]) {
        acc[key] = { id: key, places: 0, name, allowedYears: [], infos: [] }
      }

      if (id !== INCLUSION_LINE_KEY) {
        acc[INCLUSION_LINE_KEY].places -= quota.total
      }

      if (nullAcc[key] !== undefined) {
        acc[key].infos.push(
          <ClickableIcon
            key='null-quotas-icon'
            className='circular-icon z-200'
            icon={faInfoCircle}
            title={t('school_quotas_table.infos.quotas_null')}
            onClick={() => setNullQuotasKey(key)}
          />
        )
      }

      acc[key].places += quota.total

      return acc
    }, { [INCLUSION_LINE_KEY]: { id: -1, places: 0, name: '*', allowedYears: [], infos: [] } })

    if (studentPathways.length) {
      studentPathways.forEach(sp => {
        const section = sp.sectionToYear.section.id
        const group = sectionGroups.find(group => includeSection(group, section))

        const laneKey = !isUndefined(group)
          ? getLaneKey(GROUP, group)
          : getLaneKey(SECTION, section)

        const key = linesConfig[laneKey] ? laneKey : INCLUSION_LINE_KEY

        linesConfig[key].allowedYears.push(sp.sectionToYear)
      })
    }

    Object.keys(linesConfig).forEach(key => {
      const { type } = getKeyConfig(key)
      const { allowedYears, places } = linesConfig[key]

      linesConfig[key].tooltip = <SectionTooltip sectionToYears={allowedYears} places={places} type={type} />
    })

    const lanes = Object.values(linesConfig)
    const inclusions = lanes.shift()

    return [inclusions].concat(sortLanes(lanes))
  }, [quotasMap.places, quotasMap.quotasNull, studentPathways, sectionGroups])

  const quotasNullMap = useMemo(() => {
    const lineKeys = lines.reduce((acc, line) => {
      acc[getLaneKey(line.type, line.id)] = true

      return acc
    }, {})
    return quotasMap.quotasNull.reduce((acc, quota) => {
      const { type, id } = getLaneConfig(quota)
      let key = getLaneKey(type, id)

      if (key !== INCLUSION_LINE_KEY && isUndefined(lineKeys[key])) {
        key = INCLUSION_LINE_KEY
      }

      if (!acc[key]) {
        acc[key] = []
      }

      acc[key].push(quota)

      return acc
    }, {})
  }, [quotasMap.quotasNull, lines])

  const data = useMemo(() => getQuotasMatrix(
    lines,
    periods.weeks,
    quotasMap.quotas,
    quota => ({
      style: getDivStyle(colorsMap[quota.school.id] ?? DEFAULT_COLOR),
      actions: quota.isDesiderata ? [
        <PopoverIconButton key='accept' icon={faCheck} onConfirm={() => quotaHandler.acceptDesiderata(quota)} tooltipTitle='' content='quota_desiderata_form.button.accept' title='common.accept' storageKey='accept-desiderata-alert' />,
        <PopoverIconButton key='refuse' icon={faXmark} onConfirm={() => quotaHandler.refuseDesiderata(quota)} tooltipTitle='' content='quota_desiderata_form.button.refuse' title='common.delete' />
      ] : [],
      draggableAxes: quota.isDesiderata ? [Axes.VERTICAL] : [Axes.VERTICAL, Axes.HORIZONTAL],
      resizableAxes: quota.isDesiderata ? [Axes.VERTICAL] : Object.values(Axes)
    }),
    quota => <SchoolQuotaDisplayer quota={quota} overlay={tableConfig.overlay} isDesiderata={false} />,
    LOCAL_STORAGE_KEYS.QUOTAS_TABLE.QUOTAS_MATRIX
  ), [quotasMap.quotas, periods.weeks, lines, tableConfig.overlay, colorsMap, quotaHandler.acceptDesiderata, quotaHandler.refuseDesiderata])

  const holidayWeeks = useMemo(() => {
    if (holidayPeriods.length === 0) return []

    const holidayWeeksIndexes = periods.weeks.reduce((acc, week, index) => {
      if (holidayPeriods.some(holiday => holiday.includes(week))) {
        acc.push(index)
      }

      return acc
    }, [])

    return holidayWeeksIndexes
  }, [holidayPeriods, periods.weeks])

  const handleSelection = useCallback((event, selectedQuota) => {
    if (isFunction(onSelect)) {
      onSelect(selection => newSelection(selection, selectedQuota, event))
    }
  }, [onSelect])

  const handleUpsert = useCallback(quota => dataUpsert(quota, firstOrDefault(selectedQuotas), quotaHandler.upsert, areQuotaEquivalent), [selectedQuotas, quotaHandler.upsert])

  const handleAction = useCallback((event, quota) => {
    if (event === Events.DRAG_STOP) {
      handleUpsert(quota)
    } else if (event === Events.STRICT_VERTICAL_DRAG_STOP) {
      onChange(quota, Actions.UPDATE)
    } else if (event === Events.DOUBLE_CLICK || event === Events.LONG_CLICK) {
      if (isOptimistic(quota)) {
        quota.sector = sector
        quota.school = school
      }

      if (event === Events.LONG_CLICK && isYearly) {
        quota.endDate = getTimezoneLessMoment(quota.startDate).add(5, 'years')
      }

      if (isObject(school) && isOptimistic(quota)) {
        handleUpsert(quota)
      } else {
        setFormHandler(handler => ({
          ...handler,
          data: quota,
          callback: onChange,
          type: quota.isDesiderata ? Types.DESIDERATA : Types.QUOTA
        }))
      }
    } else if (event === Events.DELETE_PRESSED) {
      if (quota.isDesiderata) {
        onError(t('quota.delete.desiderata'))
      } else {
        deleteData(selectedQuotas, quotaHandler.delete)
      }
    }
  }, [sector, school, selectedQuotas, isYearly, handleUpsert, onChange, quotaHandler.delete, setFormHandler, t])

  const handleEvent = useCallback((rawEvent, rawData, key) => {
    if (!sector) {
      notification.warn({
        message: t('school_quotas_table.handle_event.no_sector_selected'),
        placement: 'bottomRight'
      })

      return
    }

    const seed = getSeed(key, rawData)
    const { quota, event } = quotaEvent(data.data[key] ?? [], data.matrix[key] ?? [], periods, rawEvent, rawData, seed, LOCAL_STORAGE_KEYS.QUOTAS_TABLE.QUOTAS_MATRIX)

    handleAction(event, quota)
    handleSelection(event, quota)
  }, [sector, periods, handleAction, handleSelection, data.data, data.matrix])

  const swimlanes = useMemo(() => {
    const lanes = [...lines]
    const inclusions = lanes.shift()

    const willContainElements = isNonEmptyArray(data.data[INCLUSION_LINE_KEY]) || isNonEmptyArray(data.overflows[INCLUSION_LINE_KEY])

    const initialLane = isPositive(inclusions.places, true) || willContainElements ? [inclusions] : []

    return initialLane.concat(lanes)
  }, [data, lines])

  const firstLaneColor = useMemo(() => swimlanes.find(l => l.id === INCLUSION_LINE_KEY)?.places === 0 ? ColorsPalette.GREY : undefined, [swimlanes])

  return (
    <>
      <HighlightedColumnsProvider>
        <DragTable
          firstLaneColor={firstLaneColor}
          disabled={!sector}
          columns={periods.months}
          lines={swimlanes}
          data={data.data}
          overflows={data.overflows}
          config={tableConfig}
          highlightedColumns={holidayWeeks}
          title={t('common.sections')}
          onEvent={handleEvent}
        />
      </HighlightedColumnsProvider>
      <SchoolQuotasDrawer
        mode={QuotaFormModes.NULL}
        quotas={quotasNullMap[nullQuotasKey] ?? []}
        seed={{ sector, section: nullQuotasKey === -1 ? null : { id: nullQuotasKey } }}
        studentPathways={studentPathways}
        title={t('school_quotas_view.drawers.quotas_null.title', { name: sector?.name ?? '' })}
        visible={nullQuotasKey !== null}
        onChange={onChange}
        onClose={() => setNullQuotasKey(null)}
      />
    </>
  )
}

export default SchoolQuotasTable
